import React from "react";
import Header from "@/components/Header";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import Footer from "@/components/Footer";
import Container from "@/components/blocks/Container";
import Link from "next/link";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import DocTitle from "@/components/docs/DocTitle";
import DocSection from "@/components/docs/DocSection";
import DocCodeBlock from "@/components/docs/DocCodeBlock";
import DocAlert from "@/components/docs/DocAlert";
import DocNavFooter from "@/components/docs/DocNavFooter";

export default function TerraformDocsPage() {
  return (
    <>
      <Head>
        {generateNextSeo({
          title: "Terraform Provider - Uptime Monitor Documentation",
          description:
            "Manage your Uptime Monitor infrastructure as code with our official Terraform provider.",
          canonical: "https://uptime-monitor.io/docs/terraform",
          openGraph: {
            title: "Terraform Provider - Uptime Monitor",
            description: "Infrastructure as Code for Uptime Monitor with Terraform",
            url: "https://uptime-monitor.io/docs/terraform",
          },
        })}
      </Head>
      <div className={docsComponents.pageContainer}>
        <Header />
        <main className="flex-1">
          <Container>
            <div className="mx-auto max-w-4xl px-4 py-12">
              {/* Breadcrumb */}
              <nav className="mb-8">
                <ol className="flex items-center space-x-2 text-sm">
                  <li>
                    <Link href="/docs" className={docsColors.link.muted}>
                      Documentation
                    </Link>
                  </li>
                  <li className={docsColors.text.muted}>/</li>
                  <li className={docsColors.text.heading}>Terraform</li>
                </ol>
              </nav>

              <DocTitle
                title="Terraform Provider"
                description="Infrastructure as Code for Uptime Monitor"
              />

              <DocAlert variant="info" className="mb-8">
                <p>
                  <a
                    href="https://registry.terraform.io/providers/uptime-monitor-io/uptime/latest/docs"
                    className={docsColors.link.primary}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <strong>Full documentation on HashiCorp Registry →</strong>
                  </a>
                  {" • "}
                  <a
                    href="https://github.com/uptime-monitor-io/terraform-provider-uptime"
                    className={docsColors.link.primary}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub
                  </a>
                </p>
              </DocAlert>

              <DocSection title="Complete Example">
                <DocCodeBlock>
                  <pre className="text-sm">
                    <code>{`terraform {
  required_providers {
    uptime = {
      source  = "uptime-monitor-io/uptime"
      version = "~> 0.0.9"
    }
  }
}

provider "uptime" {
  api_key = var.uptime_api_key  # or use env: UPTIME_API_KEY
}

# Email contact
resource "uptime_contact" "email" {
  name             = "DevOps Team"
  channel          = "email"
  down_alerts_only = false
  
  email_settings = {
    email = "devops@example.com"
  }
}

# Slack integration
resource "uptime_contact" "slack" {
  name    = "Slack #alerts"
  channel = "slack"
  
  slack_settings = {
    webhook_url = var.slack_webhook_url
  }
}

# PagerDuty for critical alerts
resource "uptime_contact" "pagerduty" {
  name    = "PagerDuty On-Call"
  channel = "pagerduty"

  pagerduty_settings = {
    integration_key        = var.pagerduty_key
    auto_resolve_incidents = true
  }
}

# Discord channel
resource "uptime_contact" "discord" {
  name    = "Discord #monitoring"
  channel = "discord"

  discord_settings = {
    webhook_url = var.discord_webhook_url
  }
}

# Webhook for custom integrations
resource "uptime_contact" "webhook" {
  name    = "Custom Webhook"
  channel = "webhook"

  webhook_settings = {
    url = "https://example.com/webhook"
  }
}

# HTTPS monitor with all options
resource "uptime_monitor" "api" {
  name           = "Production API"
  url            = "https://api.example.com/health"
  type           = "https"
  check_interval = 60      # seconds
  timeout        = 30      # seconds
  fail_threshold = 2       # regions must fail
  regions        = ["us-east-1", "us-west-1", "eu-west-1"]
  
  contacts = [
    uptime_contact.email.id,
    uptime_contact.slack.id,
    uptime_contact.pagerduty.id
  ]
  
  https_settings = {
    method                       = "POST"
    expected_status_codes        = "200,201"
    check_certificate_expiration = true
    follow_redirects             = true
    
    # Authentication
    basic_auth_username = var.api_username
    basic_auth_password = var.api_password
    
    # Headers
    headers = {
      "Content-Type" = "application/json"
      "X-API-Key"    = var.api_key
    }
    
    # Request body
    body = jsonencode({
      test = true
    })
    
    # Response validation
    expected_response_body = "\\"status\\":\\"ok\\""
    expected_response_headers = {
      "X-API-Version" = "v2"
    }
  }
}

# TCP monitor
resource "uptime_monitor" "database" {
  name           = "PostgreSQL"
  url            = "db.example.com:5432"
  type           = "tcp"
  check_interval = 120
  timeout        = 10
  regions        = ["us-east-1"]
  contacts       = [uptime_contact.email.id]
  
  tcp_settings = {}
}

# Ping monitor
resource "uptime_monitor" "gateway" {
  name           = "Network Gateway"
  url            = "10.0.0.1"
  type           = "ping"
  check_interval = 60
  timeout        = 5
  regions        = ["us-east-1"]
  contacts       = [uptime_contact.email.id]
  
  ping_settings = {}
}

# Status page with custom domain
resource "uptime_status_page" "public" {
  name          = "Service Status"
  custom_domain = "status.example.com"  # Add CNAME → status.uptime-monitor.io
  
  monitors = [
    uptime_monitor.api.id,
    uptime_monitor.database.id,
    uptime_monitor.gateway.id
  ]
  
  period                = 30   # 7, 30, or 90 days
  show_incident_reasons = true
}

# Protected internal status page
resource "uptime_status_page" "internal" {
  name       = "Internal Systems"
  basic_auth = "\${var.status_username}:\${var.status_password}"
  
  monitors = [
    uptime_monitor.database.id,
    uptime_monitor.gateway.id
  ]
  
  period                = 90
  show_incident_reasons = true
}

# Data sources to reference existing resources
data "uptime_monitor" "existing" {
  id = "monitor_id_here"
}

data "uptime_account" "current" {}

# Outputs
output "status_page_url" {
  value = uptime_status_page.public.url
}

output "monitor_count" {
  value = data.uptime_account.current.max_monitors
}`}</code>
                  </pre>
                </DocCodeBlock>
              </DocSection>

              <DocSection title="Available Regions">
                <div className="flex flex-wrap gap-2">
                  {[
                    "us-east-1",
                    "us-east-2",
                    "us-west-1",
                    "us-west-2",
                    "sa-east-1",
                    "af-south-1",
                    "ap-east-1",
                    "ap-southeast-1",
                    "ap-northeast-1",
                    "ap-northeast-2",
                    "ap-southeast-2",
                    "eu-central-1",
                    "eu-west-1",
                    "eu-north-1",
                    "eu-central-2",
                  ].map((region) => (
                    <code
                      key={region}
                      className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded"
                    >
                      {region}
                    </code>
                  ))}
                </div>
              </DocSection>

              <DocSection title="Import Existing">
                <DocCodeBlock>
                  <pre className="text-sm">
                    <code>{`terraform import uptime_monitor.api MONITOR_ID
terraform import uptime_contact.email CONTACT_ID
terraform import uptime_status_page.public STATUS_PAGE_ID`}</code>
                  </pre>
                </DocCodeBlock>
              </DocSection>

              <DocNavFooter
                prev={{
                  href: "/docs/cli",
                  text: "CLI Tool",
                }}
                next={{
                  href: "/docs/best-practices",
                  text: "Best Practices",
                }}
              />
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    </>
  );
}
